import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { ChartData, ChartMarker, TickerDetails } from '../chart.interface';
import { environment } from '../../../environments/environment';
import { baseUrl } from '../../../environments/environment';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class MarketDataService {
  private readonly MBOUM_BASE_URL = environment.apiBaseUrl;
  private readonly MBOUM_API_KEY = environment.apiKey;

  private dataCache = new Map<string, { data: any; timestamp: number }>();
  private readonly CACHE_TTL = environment.cacheTTL;

  private tickerDetailsSubject = new BehaviorSubject<TickerDetails | null>(null);
  public tickerDetails$ = this.tickerDetailsSubject.asObservable();

  // Smart interval mapping based on time range
  // Note: MBOUM API supports 1m interval but may need different range values
  private readonly RANGE_TO_INTERVAL_MAPPING: Record<string, string> = {
    '1d': '15m',   // 1 Day -> 15 min candles (faster load; user can choose 5m/30m)
    '5d': '15m',   // 5 Days -> 15 minute candles (good short-term granularity)  
    '1m': '1d',    // 1 Month -> 1 day candles (proper for monthly view)
    '6m': '1d',    // 6 Months -> 1 day candles
    'ytd': '1d',   // Year to Date -> 1 day candles
    '1y': '1d',    // 1 Year -> 1 day candles
    '5y': '1wk',   // 5 Years -> 1 week candles
    'all': '1mo'   // All Time -> 1 month candles
  };

  // MBOUM supported intervals with their valid ranges
  private readonly SUPPORTED_INTERVALS: Record<string, { label: string; ranges: string[]; maxRange: string; description: string }> = {
    '5m': { label: '5m', ranges: ['1d', '5d'], maxRange: '5d', description: '5 mins candles. Range: 5 Days' },
    '15m': { label: '15m', ranges: ['1d', '5d', '1m', '6m', 'ytd', '1y'], maxRange: '1y', description: '15 mins candles. Range: 1 Year' },
    '30m': { label: '30m', ranges: ['1d', '5d', '1m', '6m', 'ytd', '1y'], maxRange: '1y', description: '30 mins candles. Range: 1 Year' },
    '1d': { label: '1D', ranges: ['1d', '5d', '1m', '6m', 'ytd', '1y', '2y', '5y'], maxRange: '5y', description: '1 day candles. Range: 5 Years' },
    '1wk': { label: '1W', ranges: ['6m', '1y', '2y', '5y'], maxRange: '5y', description: '1 week candles. Range: 5 Years' },
    '1mo': { label: '1M', ranges: ['1y', '2y', '5y', '10y', 'all'], maxRange: '10y', description: '1 month candles. Range: 10 Years' }
  };

  // Get optimal interval for a given range
  private getOptimalInterval(range: string): string {
    return (this.RANGE_TO_INTERVAL_MAPPING as Record<string, string>)[range] || '1d';
  }

  // Get supported intervals for UI dropdown
  getSupportedIntervals(): Array<{ value: string; label: string; description: string }> {
    return Object.entries(this.SUPPORTED_INTERVALS).map(([value, config]) => ({
      value,
      label: config.label,
      description: config.description
    }));
  }

  // Check if interval-range combination is valid
  isValidIntervalRange(interval: string, range: string): boolean {
    const config = this.SUPPORTED_INTERVALS[interval];
    return config ? config.ranges.includes(range) : false;
  }

  // Get valid ranges for a specific interval
  getValidRangesForInterval(interval: string): string[] {
    return this.SUPPORTED_INTERVALS[interval]?.ranges || [];
  }

  constructor(private http: HttpClient) {}

  // Search for symbols using Mboum API
  async searchSymbols(query: string): Promise<any[]> {
    try {
      if (!query || query.trim().length < 2) {
        return [];
      }
      const searchQuery = query.trim().toUpperCase();

      const url = `${this.MBOUM_BASE_URL}/v1/markets/search?search=${encodeURIComponent(searchQuery)}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.MBOUM_API_KEY}`,
          'Content-Type': 'application/json',
          'User-Agent': 'TEN-Charting/1.0'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Extract the body array from the response
      const results = data?.body || [];
      return Array.isArray(results) ? results : [];
    } catch (error) {
      // Return empty array on error to prevent crashes
      return [];
    }
  }

  // Search tickers from backend local database for header dropdown.
  async searchTickersFromMF(query: string): Promise<any[]> {
    try {
      if (!query || query.trim().length < 2) {
        return [];
      }
      const searchQuery = query.trim();
      const token =
        localStorage.getItem('token') ||
        localStorage.getItem('authToken') ||
        localStorage.getItem('accessToken') ||
        sessionStorage.getItem('token') ||
        '';

      const headersConfig: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headersConfig['Authorization'] = `Bearer ${token}`;
        headersConfig['token'] = token;
      }
      const headers = new HttpHeaders(headersConfig);

      const response = await firstValueFrom(
        this.http.get<any>(`${baseUrl}/trade/getTickersFromMF?search=${encodeURIComponent(searchQuery)}`, { headers })
      );
      const list = response?.data;
      return Array.isArray(list) ? list : [];
    } catch {
      return [];
    }
  }

  async loadChartData(symbol: string, range: string = '5y', manualInterval?: string, limit?: number): Promise<ChartData[]> {
    try {
      // Null safety check for symbol
      if (!symbol || typeof symbol !== 'string' || symbol.trim().length === 0) {
        return [];
      }

      const interval = manualInterval || this.getOptimalInterval(range);
      
      
      // Validate interval-range combination
      if (manualInterval && !this.isValidIntervalRange(interval, range)) {
        const optimalInterval = this.getOptimalInterval(range);
        return this.loadChartData(symbol, range, optimalInterval, limit);
      }
      
      // Include limit in cache key to ensure different limits get different cache entries
      const cacheKey = `chart_${symbol}_${range}_${interval}_${limit || 'default'}`;
      
      // Clear cache if needed for fresh data or if limit changed significantly
      const cached = this.getFromCache(cacheKey);
      if (cached && Array.isArray(cached)) {
        // If we have cached data but the limit is significantly different, clear it
        if (limit && Math.abs(cached.length - limit) > 100) {
          this.removeFromCache(cacheKey);
        } else {
          return cached;
        }
      }

      const response = await this.fetchMarketDataWithInterval(symbol, range, interval, limit);
      
      const processedData = this.processChartData(response, symbol);
      
      // If no data or API fails, use fallback data
      if (processedData.length === 0) {
        const fallbackData = this.generateFallbackData(symbol, range);
        this.setCache(cacheKey, fallbackData);
        this.updateTickerDetails(fallbackData, symbol);
        return fallbackData;
      }
      
      this.setCache(cacheKey, processedData);
      this.updateTickerDetails(processedData, symbol);
      return processedData;
    } catch (error) {
      try {
        const fallbackData = this.generateFallbackData(symbol, range);
        return fallbackData;
      } catch (fallbackError) {
        return [];
      }
    }
  }

  async loadCalendarEvents(symbol: string): Promise<ChartMarker[]> {
    try {
      if (!symbol || typeof symbol !== 'string' || symbol.trim().length === 0) {
        return [];
      }

      const cacheKey = `calendar_${symbol}`;
      const cached = this.getFromCache(cacheKey);
      if (cached) {
        return cached;
      }

      const response = await this.fetchCalendarEvents(symbol);
      const processedEvents = this.processCalendarEvents(response);
      this.setCache(cacheKey, processedEvents);
      return processedEvents;
    } catch (error) {
      return [];
    }
  }

  private async fetchMarketDataWithInterval(symbol: string, range: string, interval: string, limit?: number): Promise<any> {
    try {
      // Use provided limit or default to 1000
      const dataLimit = limit || 1000;
      
      // Try v2 first (newer API version) - uses ticker, interval, limit
      let url = `${this.MBOUM_BASE_URL}/v2/markets/stock/history?ticker=${symbol}&interval=${interval}&limit=${dataLimit}`;
      
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${this.MBOUM_API_KEY}`,
          'Content-Type': 'application/json',
          'User-Agent': 'TEN-Charting/1.0'
        }
      });

      // Handle different HTTP status codes
      if (!response.ok) {
        
        // For 422 (Unprocessable Content) or other client errors with intraday intervals, try alternatives
        if ((response.status === 422 || response.status >= 400) && ['1m', '5m', '15m', '30m'].includes(interval)) {
          
          // Try without the limit parameter for intraday
          const alternativeUrl = `${this.MBOUM_BASE_URL}/v2/markets/stock/history?ticker=${symbol}&interval=${interval}`;
          
          const altResponse = await fetch(alternativeUrl, {
            headers: {
              'Authorization': `Bearer ${this.MBOUM_API_KEY}`,
              'Content-Type': 'application/json',
              'User-Agent': 'TEN-Charting/1.0'
            }
          });
          
          if (altResponse.ok) {
            const altData = await altResponse.json();
            if (altData.success !== false) {
              return altData;
            }
          }
          
          // Try v1 API as final fallback for intraday
          const v1Url = `${this.MBOUM_BASE_URL}/v1/markets/stock/history?symbol=${symbol}&interval=${interval}&range=1d`;
          
          const v1Response = await fetch(v1Url, {
            headers: {
              'Authorization': `Bearer ${this.MBOUM_API_KEY}`,
              'Content-Type': 'application/json',
              'User-Agent': 'TEN-Charting/1.0'
            }
          });
          
          if (v1Response.ok) {
            const v1Data = await v1Response.json();
            if (v1Data.success !== false) {
              return v1Data;
            }
          }
        }
        
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Log the full API response for debugging
      
      // If we get "No data available" for intraday, try alternative approaches
      if (data.success === false && ['1m', '5m', '15m', '30m'].includes(interval)) {
        
        // Try without the limit parameter for intraday
        const alternativeUrl = `${this.MBOUM_BASE_URL}/v2/markets/stock/history?ticker=${symbol}&interval=${interval}`;
        
        const altResponse = await fetch(alternativeUrl, {
          headers: {
            'Authorization': `Bearer ${this.MBOUM_API_KEY}`,
            'Content-Type': 'application/json',
            'User-Agent': 'TEN-Charting/1.0'
          }
        });
        
        if (altResponse.ok) {
          const altData = await altResponse.json();
          if (altData.success !== false) {
            return altData;
          }
        }
        
        // Try with a smaller limit for intraday
        const alternativeLimitUrl = `${this.MBOUM_BASE_URL}/v2/markets/stock/history?ticker=${symbol}&interval=${interval}&limit=100`;
        
        const altLimitResponse = await fetch(alternativeLimitUrl, {
          headers: {
            'Authorization': `Bearer ${this.MBOUM_API_KEY}`,
            'Content-Type': 'application/json',
            'User-Agent': 'TEN-Charting/1.0'
          }
        });
        
        if (altLimitResponse.ok) {
          const altLimitData = await altLimitResponse.json();
          if (altLimitData.success !== false) {
            return altLimitData;
          }
        }
        
        // If v2 fails, try v1 as final fallback
        const v1Url = `${this.MBOUM_BASE_URL}/v1/markets/stock/history?symbol=${symbol}&interval=${interval}&range=${range}`;
        
        const v1Response = await fetch(v1Url, {
          headers: {
            'Authorization': `Bearer ${this.MBOUM_API_KEY}`,
            'Content-Type': 'application/json',
            'User-Agent': 'TEN-Charting/1.0'
          }
        });
        
        if (v1Response.ok) {
          const v1Data = await v1Response.json();
          if (v1Data.success !== false) {
            return v1Data;
          }
        }
        
      }
      
      return data;
    } catch (error) {
      throw error; // Re-throw to be handled by calling method
    }
  }

  // Keep the old method for backward compatibility
  private async fetchMarketData(symbol: string, range: string = '5y'): Promise<any> {
    const interval = this.getOptimalInterval(range);
    return this.fetchMarketDataWithInterval(symbol, range, interval);
  }

  private async fetchCalendarEvents(symbol: string): Promise<any> {
    const url = `${this.MBOUM_BASE_URL}/v1/markets/stock/modules?symbol=${symbol}&module=calendar-events`;
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${this.MBOUM_API_KEY}`,
        'Content-Type': 'application/json',
        'User-Agent': 'TEN-Charting/1.0'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  private processChartData(data: any, symbol?: string): ChartData[] {
    
    // Enhanced null safety checks
    if (!data || typeof data !== 'object') {
      return [];
    }

    // Check different possible data structures
    let chartData: any[] = [];
    let rawDataObject: any = null;
    
    if (data.data && Array.isArray(data.data)) {
      chartData = data.data;
    } else if (data.result && Array.isArray(data.result)) {
      chartData = data.result;
    } else if (data.body && data.body.data && Array.isArray(data.body.data)) {
      chartData = data.body.data;
    } else if (data.body && typeof data.body === 'object' && data.body !== null) {
      // Handle the object structure we're seeing from the API
      rawDataObject = data.body;
    } else if (Array.isArray(data)) {
      chartData = data;
    } else {
      return [];
    }

    // If we have an object structure, convert it to array with null safety
    if (rawDataObject && typeof rawDataObject === 'object' && rawDataObject !== null && Object.keys(rawDataObject).length > 0) {
      chartData = Object.values(rawDataObject);
    }

    if (!Array.isArray(chartData) || chartData.length === 0) {
      return [];
    }


    // Convert the data to our expected format
    const processedData: ChartData[] = chartData
      .map((item: any): ChartData | null => {
        // Null safety check for item
        if (!item || typeof item !== 'object') {
          return null;
        }

        // Handle different date formats with null safety
        let timeValue: number;
        if (typeof item.timestamp_unix === 'number' && !isNaN(item.timestamp_unix)) {
          // MBOUM API uses timestamp_unix (already in seconds)
          timeValue = item.timestamp_unix;
        } else if (typeof item.date_utc === 'number' && !isNaN(item.date_utc)) {
          timeValue = item.date_utc;
        } else if (item.date && typeof item.date === 'string') {
          const dateValue = new Date(item.date);
          timeValue = isNaN(dateValue.getTime()) ? 0 : dateValue.getTime() / 1000;
        } else if (typeof item.timestamp === 'number' && !isNaN(item.timestamp)) {
          timeValue = item.timestamp;
        } else if (item.time && typeof item.time === 'string') {
          const timeDate = new Date(item.time);
          timeValue = isNaN(timeDate.getTime()) ? 0 : timeDate.getTime() / 1000;
        } else {
          return null;
        }

        // Validate time value
        if (isNaN(timeValue) || timeValue <= 0) {
          return null;
        }

        // Parse numeric values with null safety
        const open = parseFloat(item.open || item.o || '0');
        const high = parseFloat(item.high || item.h || '0');
        const low = parseFloat(item.low || item.l || '0');
        const close = parseFloat(item.close || item.c || '0');
        const volume = parseInt(item.volume || item.v || '0');

        // Validate price values
        if (isNaN(open) || isNaN(high) || isNaN(low) || isNaN(close) || isNaN(volume)) {
          return null;
        }

        return {
          time: timeValue as any,
          open: open,
          high: high,
          low: low,
          close: close,
          volume: volume
        };
      })
      .filter((item): item is ChartData => item !== null) // Remove null items with type guard
      .sort((a, b) => Number(a.time) - Number(b.time)); // Sort by time

    if (processedData.length > 0) {
      const minPrice = Math.min(...processedData.map(d => d.low));
      const maxPrice = Math.max(...processedData.map(d => d.high));
      const latestPrice = processedData[processedData.length - 1].close;
      
      
      // Add price validation warnings for common symbols
      if (symbol) {
        this.validatePriceRange(symbol, latestPrice);
      }
    }

    return processedData;
  }

  private processCalendarEvents(data: any): ChartMarker[] {
    if (!data || !data.calendarEvents) {
      return [];
    }

    return data.calendarEvents.map((event: any) => ({
      id: `event_${event.date}_${event.type}`,
      time: new Date(event.date).getTime() / 1000,
      position: 'aboveBar',
      color: this.getEventColor(event.type),
      shape: this.getEventShape(event.type),
      text: event.type,
      category: event.type,
      title: event.title,
      description: event.description,
      source: 'api',
      editable: false
    }));
  }

  private getEventColor(type: string): string {
    const colors = {
      'earnings': '#4CAF50',
      'dividend': '#2196F3',
      'split': '#FF9800',
      'default': '#757575'
    };
    return colors[type as keyof typeof colors] || colors.default;
  }

  private getEventShape(type: string): 'circle' | 'square' | 'arrowUp' | 'arrowDown' {
    const shapes: Record<string, 'circle' | 'square' | 'arrowUp' | 'arrowDown'> = {
      'earnings': 'circle',
      'dividend': 'square',
      'split': 'arrowUp',
      'default': 'circle'
    };
    return shapes[type] || shapes['default'];
  }

  private updateTickerDetails(data: ChartData[], symbol: string): void {
    if (data.length === 0) return;

    const latest = data[data.length - 1];
    const previous = data[data.length - 2];

    // Debug logging for price validation

    const tickerDetails: TickerDetails = {
      symbol: symbol.toUpperCase(),
      companyName: this.getCompanyName(symbol),
      open: latest.open,
      high: latest.high,
      low: latest.low,
      close: latest.close,
      volume: latest.volume || 0,
      previousClose: previous ? previous.close : latest.close,
      change: previous ? latest.close - previous.close : 0,
      changePercent: previous ? ((latest.close - previous.close) / previous.close) * 100 : 0,
      lastUpdated: new Date()
    };

    // Ticker details ready
    this.tickerDetailsSubject.next(tickerDetails);
  }

  private getCompanyName(symbol: string): string {
    const companyNames: { [key: string]: string } = {
      'AAPL': 'Apple Inc.',
      'GOOGL': 'Alphabet Inc.',
      'MSFT': 'Microsoft Corporation',
      'AMZN': 'Amazon.com Inc.',
      'TSLA': 'Tesla Inc.',
      'META': 'Meta Platforms Inc.',
      'NVDA': 'NVIDIA Corporation',
      'NFLX': 'Netflix Inc.',
      'BABA': 'Alibaba Group Holding Ltd.',
      'V': 'Visa Inc.'
    };
    return companyNames[symbol.toUpperCase()] || `${symbol.toUpperCase()} Corporation`;
  }

  private getFromCache(key: string): any | null {
    const cached = this.dataCache.get(key);
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      return cached.data;
    }
    return null;
  }

  private setCache(key: string, data: any): void {
    this.dataCache.set(key, { data, timestamp: Date.now() });
  }

  private removeFromCache(key: string): void {
    this.dataCache.delete(key);
  }

  private generateFallbackData(symbol: string, range: string = '5y'): ChartData[] {
    
    const chartData: ChartData[] = [];
    const currentDate = new Date();
    const basePrice = 150; // Base price for fallback data generation
    
    // Calculate number of trading days based on range
    let numberOfTradingDays: number;
    switch (range) {
      case '1d':
        numberOfTradingDays = 1;
        break;
      case '5d':
        numberOfTradingDays = 5;
        break;
      case '1m':
        numberOfTradingDays = 22; // ~1 month
        break;
      case '3m':
        numberOfTradingDays = 66; // ~3 months
        break;
      case '6m':
        numberOfTradingDays = 126; // ~6 months
        break;
      case 'ytd':
        // Calculate trading days from January 1 of current year to today
        const startOfCurrentYear = new Date(currentDate.getFullYear(), 0, 1); // January 1 of current year
        let ytdTradingDays = 0;
        const tempDate = new Date(startOfCurrentYear);
        
        while (tempDate <= currentDate) {
          // Skip weekends (0 = Sunday, 6 = Saturday)
          if (tempDate.getDay() !== 0 && tempDate.getDay() !== 6) {
            ytdTradingDays++;
          }
          tempDate.setDate(tempDate.getDate() + 1);
        }
        
        numberOfTradingDays = Math.max(1, ytdTradingDays); // Ensure at least 1 day
        break;
      case '1y':
        numberOfTradingDays = 252; // ~1 year
        break;
      case '2y':
        numberOfTradingDays = 504; // ~2 years
        break;
      case '5y':
        numberOfTradingDays = 1260; // ~5 years
        break;
      case '10y':
        numberOfTradingDays = 2520; // ~10 years
        break;
      case 'max':
        numberOfTradingDays = 5040; // ~20 years
        break;
      default:
        numberOfTradingDays = 252; // Default to 1 year to avoid excessive data
    }
    
    
    // Generate trading days of data
    let dataStartDate: Date;
    if (range === 'ytd') {
      // For YTD, start from January 1 of current year
      dataStartDate = new Date(currentDate.getFullYear(), 0, 1);
    } else {
      // For other ranges, go backwards from current date
      dataStartDate = new Date(currentDate);
      dataStartDate.setDate(dataStartDate.getDate() - (numberOfTradingDays - 1));
    }
    
    let iterationDate = new Date(dataStartDate);
    let pointsGenerated = 0;
    
    while (pointsGenerated < numberOfTradingDays && iterationDate <= currentDate) {
      // Skip weekends
      if (iterationDate.getDay() === 0 || iterationDate.getDay() === 6) {
        iterationDate.setDate(iterationDate.getDate() + 1);
        continue;
      }
      
      const randomFactor = 0.02; // 2% daily volatility
      const longTermTrend = Math.sin(pointsGenerated / 200) * 0.15; // Long-term cycle
      const mediumTermTrend = Math.sin(pointsGenerated / 50) * 0.08; // Medium-term trend
      const dailyChange = (Math.random() - 0.5) * randomFactor + longTermTrend * 0.01 + mediumTermTrend * 0.005;
      
      const previousClose = pointsGenerated === 0 ? basePrice : chartData[chartData.length - 1]?.close || basePrice;
      const open = previousClose * (1 + (Math.random() - 0.5) * 0.005);
      const close = open * (1 + dailyChange);
      const high = Math.max(open, close) * (1 + Math.random() * 0.015);
      const low = Math.min(open, close) * (1 - Math.random() * 0.015);
      const volume = Math.floor(Math.random() * 15000000) + 500000; // Varied volume
      
      chartData.push({
        time: (iterationDate.getTime() / 1000) as any,
        open: parseFloat(open.toFixed(2)),
        high: parseFloat(high.toFixed(2)),
        low: parseFloat(low.toFixed(2)),
        close: parseFloat(close.toFixed(2)),
        volume: volume
      });
      
      pointsGenerated++;
      iterationDate.setDate(iterationDate.getDate() + 1);
    }
    
    return chartData;
  }

  private validatePriceRange(symbol: string, price: number): void {
    const expectedRanges: { [key: string]: { min: number; max: number; name: string } } = {
      'DDOG': { min: 100, max: 150, name: 'Datadog' },
      'AAPL': { min: 200, max: 280, name: 'Apple' },      // Updated for current market
      'GOOGL': { min: 130, max: 200, name: 'Alphabet' },   // Updated for current market
      'MSFT': { min: 300, max: 500, name: 'Microsoft' },   // Updated for current market
      'AMZN': { min: 120, max: 200, name: 'Amazon' },      // Updated for current market
      'TSLA': { min: 150, max: 350, name: 'Tesla' },
    };

    const expected = expectedRanges[symbol.toUpperCase()];
    if (expected) {
      if (price < expected.min || price > expected.max) {
      } else {
      }
    }
  }

  clearCache(): void {
    this.dataCache.clear();
  }

  // Method to get current interval for a range (useful for UI display)
  getCurrentInterval(range: string): string {
    return this.getOptimalInterval(range);
  }

  // Method to get interval description for UI
  getIntervalDescription(range: string): string {
    const interval = this.getOptimalInterval(range);
    const descriptions: Record<string, string> = {
      '1m': '1-minute candles',
      '5m': '5-minute candles', 
      '15m': '15-minute candles',
      '30m': '30-minute candles',
      '1h': '1-hour candles',
      '1d': '1-day candles',
      '1wk': '1-week candles',
      '1mo': '1-month candles'
    };
    return (descriptions as Record<string, string>)[interval] || `${interval} candles`;
  }

  /**
   * Optional fields for fullscreen quote strip (exchange, session times, pre-market).
   * Parses common provider field names from `/v1/markets/stock/quotes` payload.
   */
  async fetchExtendedQuoteData(symbol: string): Promise<Partial<TickerDetails> | null> {
    if (!symbol || typeof symbol !== 'string' || symbol.trim().length === 0) {
      return null;
    }
    try {
      const url = `${this.MBOUM_BASE_URL}/v1/markets/stock/quotes?ticker=${encodeURIComponent(symbol.trim())}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${this.MBOUM_API_KEY}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        return null;
      }
      const data = await response.json();
      const quoteData = data?.body || data;
      let tickerData: any;
      if (Array.isArray(quoteData)) {
        tickerData = quoteData[0];
      } else if (quoteData && typeof quoteData === 'object') {
        tickerData = quoteData;
      }
      if (!tickerData || typeof tickerData !== 'object') {
        return null;
      }
      return this.extractExtendedQuoteFields(tickerData);
    } catch {
      return null;
    }
  }

  private extractExtendedQuoteFields(tickerData: Record<string, unknown>): Partial<TickerDetails> | null {
    const out: Partial<TickerDetails> = {};

    const str = (v: unknown): string | undefined =>
      v !== undefined && v !== null && String(v).trim() !== '' ? String(v).trim() : undefined;

    const num = (v: unknown): number | undefined => {
      if (v === undefined || v === null || v === '') {
        return undefined;
      }
      const n = typeof v === 'number' ? v : parseFloat(String(v));
      return Number.isFinite(n) ? n : undefined;
    };

    const asDate = (v: unknown): Date | undefined => {
      if (v === undefined || v === null) {
        return undefined;
      }
      if (v instanceof Date) {
        return isNaN(v.getTime()) ? undefined : v;
      }
      if (typeof v === 'number') {
        const ms = v < 1e12 ? v * 1000 : v;
        const d = new Date(ms);
        return isNaN(d.getTime()) ? undefined : d;
      }
      const d = new Date(String(v));
      return isNaN(d.getTime()) ? undefined : d;
    };

    const exchange =
      str(tickerData['fullExchangeName']) ||
      str(tickerData['exchangeName']) ||
      str(tickerData['exchange']) ||
      str(tickerData['market']);
    if (exchange) {
      out.exchangeDisplay = exchange;
    }

    const cur = str(tickerData['currency']);
    if (cur) {
      out.currency = cur;
    }

    const rmt = asDate(
      tickerData['regularMarketTime'] ??
        tickerData['regular_market_time'] ??
        tickerData['closeTime']
    );
    if (rmt) {
      out.regularMarketTime = rmt;
    }

    const prePx = num(
      tickerData['preMarketPrice'] ??
        tickerData['pre_market_price'] ??
        tickerData['preMarketOpen'] ??
        tickerData['preMarketClose']
    );
    const preCh = num(tickerData['preMarketChange'] ?? tickerData['pre_market_change']);
    const prePct = num(
      tickerData['preMarketChangePercent'] ?? tickerData['pre_market_change_percent'] ?? tickerData['preMarketChgPct']
    );
    const preT = asDate(tickerData['preMarketTime'] ?? tickerData['pre_market_time']);

    if (prePx !== undefined) {
      out.preMarketPrice = prePx;
    }
    if (preCh !== undefined) {
      out.preMarketChange = preCh;
    }
    if (prePct !== undefined) {
      out.preMarketChangePercent = prePct;
    }
    if (preT) {
      out.preMarketTime = preT;
    }

    return Object.keys(out).length > 0 ? out : null;
  }

  // Fetch quote data for sidebar items
  async fetchQuoteData(symbol: string): Promise<{lastPrice: number, change: number, changePercent: number} | null> {
    // Null safety check for symbol
    if (!symbol || typeof symbol !== 'string' || symbol.trim().length === 0) {
      return null;
    }

    try {
      const url = `${this.MBOUM_BASE_URL}/v1/markets/stock/quotes?ticker=${symbol}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.MBOUM_API_KEY}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      
      // Handle the API response structure with null safety
      const quoteData = data?.body || data;
      
      if (!quoteData || typeof quoteData !== 'object') {
        return null;
      }
      
      // Handle array structure (data.body is an array with numeric keys)
      let tickerData: any;
      if (Array.isArray(quoteData)) {
        tickerData = quoteData[0]; // Get first element
      } else if (quoteData && typeof quoteData === 'object') {
        tickerData = quoteData;
      }
      
      if (!tickerData || typeof tickerData !== 'object') {
        return null;
      }

      const lastPrice = parseFloat(tickerData.regularMarketPrice || tickerData.price || tickerData.lastPrice || '0');
      const change = parseFloat(tickerData.regularMarketChange || tickerData.change || '0');
      const changePercent = parseFloat(tickerData.regularMarketChangePercent || tickerData.changePercent || '0');
      
      // Validate parsed values
      if (isNaN(lastPrice) || isNaN(change) || isNaN(changePercent)) {
        return null;
      }
      
      
      return {
        lastPrice,
        change,
        changePercent
      };
      
    } catch (error) {
      return null;
    }
  }

  // Fetch multiple quotes at once (comma-separated tickers)
  async fetchMultipleQuotes(symbols: string[]): Promise<{[symbol: string]: {lastPrice: number, change: number, changePercent: number}}> {
    try {
      const tickers = symbols.join(',');
      const url = `${this.MBOUM_BASE_URL}/v1/markets/stock/quotes?ticker=${tickers}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.MBOUM_API_KEY}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        return {};
      }

      const data = await response.json();
      
      // Process the response to extract individual ticker data
      const result: {[symbol: string]: {lastPrice: number, change: number, changePercent: number}} = {};
      
      // Handle different possible response structures
      if (data.body && Array.isArray(data.body)) {
        // If body is an array of ticker data
        data.body.forEach((tickerData: any) => {
          if (tickerData.symbol || tickerData.ticker) {
            const symbol = tickerData.symbol || tickerData.ticker;
            result[symbol] = {
              lastPrice: parseFloat(tickerData.regularMarketPrice || tickerData.price || tickerData.lastPrice || 0),
              change: parseFloat(tickerData.regularMarketChange || tickerData.change || 0),
              changePercent: parseFloat(tickerData.regularMarketChangePercent || tickerData.changePercent || 0)
            };
          }
        });
      } else if (data.body && typeof data.body === 'object') {
        // If body is an object with ticker keys
        Object.keys(data.body).forEach(symbol => {
          const tickerData = data.body[symbol];
          if (tickerData && typeof tickerData === 'object') {
            result[symbol] = {
              lastPrice: parseFloat(tickerData.regularMarketPrice || tickerData.price || tickerData.lastPrice || 0),
              change: parseFloat(tickerData.regularMarketChange || tickerData.change || 0),
              changePercent: parseFloat(tickerData.regularMarketChangePercent || tickerData.changePercent || 0)
            };
          }
        });
      }
      
      return result;
    } catch (error) {
      return {};
    }
  }
} 